import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Fuel, Zap, Flame, Rocket, ArrowRight, Clock, TrendingUp, Gift, ShoppingCart, Sparkles, Mic2, HeadphonesIcon, Trophy, ChevronRight, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierConfig = {
  fumes: { color: "text-zinc-400", bg: "bg-zinc-500/20", border: "border-zinc-500/40", glow: "", icon: Fuel },
  unleaded: { color: "text-neon-cyan", bg: "bg-neon-cyan/15", border: "border-neon-cyan/40", glow: "shadow-neon-cyan/20", icon: Zap },
  premium: { color: "text-neon-magenta", bg: "bg-neon-magenta/15", border: "border-neon-magenta/40", glow: "shadow-neon-magenta/20", icon: Flame },
  nitrous: { color: "text-neon-yellow", bg: "bg-neon-yellow/15", border: "border-neon-yellow/40", glow: "shadow-neon-yellow/20", icon: Rocket },
};

const OctanePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loyalty, setLoyalty] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchAll();
  }, [user]);

  const fetchAll = async () => {
    const headers = getAuthHeaders();
    try {
      const [loyaltyRes, historyRes, leaderRes] = await Promise.all([
        axios.get(`${API}/loyalty/me`, { headers, withCredentials: true }),
        axios.get(`${API}/loyalty/history`, { headers, withCredentials: true }),
        axios.get(`${API}/loyalty/leaderboard`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLoyalty(loyaltyRes.data);
      setHistory(historyRes.data);
      setLeaderboard(leaderRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="octane-page">
        <div className="text-center max-w-md">
          <Fuel className="w-16 h-16 text-neon-yellow mx-auto mb-6" />
          <h1 className="font-heading text-3xl uppercase tracking-tight mb-4">Octane Rewards</h1>
          <p className="text-zinc-400 mb-6">Log in to start earning Octane and unlock exclusive rewards.</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black font-bold uppercase">Go Home</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="octane-page">
        <div className="w-12 h-12 border-4 border-neon-yellow border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tier = loyalty?.tier?.tier || "fumes";
  const tc = tierConfig[tier] || tierConfig.fumes;
  const TierIcon = tc.icon;
  const progress = loyalty?.next_tier
    ? ((loyalty.lifetime_points - (loyalty.tier?.min_points || 0)) / (loyalty.next_tier.min_points - (loyalty.tier?.min_points || 0))) * 100
    : 100;

  return (
    <div className="min-h-screen pb-20" data-testid="octane-page">
      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-yellow/5 via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon-yellow/30 bg-neon-yellow/10 mb-6">
              <Fuel className="w-4 h-4 text-neon-yellow" />
              <span className="text-neon-yellow text-xs font-bold uppercase tracking-widest">Octane Rewards</span>
              {loyalty?.is_weekend_bonus && (
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/40 text-[10px] ml-1" data-testid="weekend-bonus-badge">
                  2X WEEKEND
                </Badge>
              )}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-4" data-testid="octane-balance-heading">
              <span className="text-neon-yellow">{loyalty?.current_points?.toLocaleString() || 0}</span>{" "}
              <span className="text-white">Octane</span>
            </h1>
            <p className="text-zinc-400 text-base max-w-lg mx-auto mb-2">
              Worth <span className="text-neon-green font-mono font-bold">${loyalty?.points_value?.toFixed(2) || "0.00"}</span> in store credit
            </p>
            <p className="text-zinc-500 text-sm">
              Earn {loyalty?.octane_per_dollar || 10} Octane for every $1 spent &middot; {loyalty?.octane_to_dollar || 100} Octane = $1 off
            </p>
          </motion.div>

          {/* Tier Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className={`mt-10 max-w-lg mx-auto glass border ${tc.border} rounded-xl p-6 shadow-lg ${tc.glow}`}
            data-testid="octane-tier-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${tc.bg} flex items-center justify-center`}>
                  <TierIcon className={`w-5 h-5 ${tc.color}`} />
                </div>
                <div>
                  <p className={`font-heading text-lg uppercase ${tc.color}`}>{loyalty?.tier?.label}</p>
                  <p className="text-zinc-500 text-xs">{loyalty?.lifetime_points?.toLocaleString()} lifetime Octane</p>
                </div>
              </div>
              {loyalty?.active_multiplier > 1 && (
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/40" data-testid="multiplier-badge">
                  {loyalty.active_multiplier}x BOOST
                </Badge>
              )}
            </div>
            {loyalty?.next_tier && (
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-zinc-400">Progress to {loyalty.next_tier.label}</span>
                  <span className="text-zinc-500">{loyalty.next_tier.points_needed?.toLocaleString()} Octane to go</span>
                </div>
                <Progress value={Math.min(progress, 100)} className="h-2" data-testid="tier-progress-bar" />
              </div>
            )}
            {!loyalty?.next_tier && (
              <p className="text-neon-yellow text-xs font-bold uppercase tracking-wider text-center">Max Tier Reached</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {loyalty?.tier?.perks?.map((perk, i) => (
                <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 text-zinc-400 border border-white/5">{perk}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex gap-1 border-b border-white/10 mb-8">
          {[
            { id: "overview", label: "How It Works" },
            { id: "history", label: "History" },
            { id: "spend", label: "Spend Octane" },
            { id: "leaderboard", label: "Leaderboard" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-xs uppercase tracking-wider font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "text-neon-yellow border-neon-yellow"
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
              data-testid={`octane-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* How It Works */}
        {activeTab === "overview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8" data-testid="octane-overview-section">
            <h2 className="font-heading text-lg uppercase tracking-wide">Earn Octane</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: ShoppingCart, title: "Every Purchase", desc: `Earn ${loyalty?.octane_per_dollar || 10} Octane per $1 spent`, highlight: "10 OCT / $1" },
                { icon: Gift, title: "Sign Up Bonus", desc: "Get 50 Octane just for creating an account", highlight: "+50 OCT" },
                { icon: Sparkles, title: "First Purchase", desc: "Double Octane on your very first order", highlight: "2X OCTANE" },
                { icon: Calendar, title: "Weekend Bonus", desc: "2x Octane every Saturday & Sunday", highlight: "2X WEEKENDS" },
                { icon: TrendingUp, title: "Tier Multiplier", desc: "Higher tiers earn more per purchase", highlight: "UP TO 2X" },
                { icon: Trophy, title: "Bonus Events", desc: "Special limited-time multiplier events", highlight: "SPECIAL" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass border border-white/5 rounded-lg p-5 hover:border-neon-yellow/20 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <item.icon className="w-5 h-5 text-neon-yellow" />
                    <span className="text-[10px] font-bold text-neon-yellow bg-neon-yellow/10 px-2 py-0.5 rounded-full">{item.highlight}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs text-zinc-500">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <h2 className="font-heading text-lg uppercase tracking-wide mt-8">Octane Tiers</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(tierConfig).map(([key, cfg], i) => {
                const tierData = {
                  fumes: { label: "Fumes", min: 0, perks: ["Early access to drops"] },
                  unleaded: { label: "Unleaded", min: 1000, perks: ["5% discount", "1.25x earning"] },
                  premium: { label: "Premium", min: 5000, perks: ["10% discount", "1.5x earning"] },
                  nitrous: { label: "Nitrous", min: 15000, perks: ["15% discount", "2x earning"] },
                }[key];
                const Icon = cfg.icon;
                const isActive = tier === key;
                return (
                  <div
                    key={key}
                    className={`glass border rounded-lg p-5 transition-all ${isActive ? `${cfg.border} ${cfg.bg}` : "border-white/5"}`}
                  >
                    <Icon className={`w-6 h-6 ${cfg.color} mb-3`} />
                    <h3 className={`font-heading text-base uppercase ${cfg.color}`}>{tierData.label}</h3>
                    <p className="text-zinc-500 text-xs mt-1">{tierData.min > 0 ? `${tierData.min.toLocaleString()} lifetime Octane` : "Starting tier"}</p>
                    <div className="mt-3 space-y-1">
                      {tierData.perks.map((p, j) => (
                        <p key={j} className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                          <ChevronRight className="w-3 h-3 text-zinc-600" />{p}
                        </p>
                      ))}
                    </div>
                    {isActive && <Badge className={`mt-3 ${cfg.bg} ${cfg.color} border-0 text-[10px]`}>CURRENT</Badge>}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* History */}
        {activeTab === "history" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="octane-history-section">
            {history.length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500">No Octane transactions yet</p>
                <Button onClick={() => navigate("/beats")} className="mt-4 bg-neon-yellow text-black font-bold uppercase text-xs">
                  Start Earning
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((h, i) => (
                  <motion.div
                    key={h.history_id || i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex items-center justify-between p-4 glass border border-white/5 rounded-lg"
                    data-testid={`octane-history-item-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        h.points > 0 ? "bg-neon-green/15" : "bg-red-500/15"
                      }`}>
                        {h.points > 0
                          ? <TrendingUp className="w-4 h-4 text-neon-green" />
                          : <ArrowRight className="w-4 h-4 text-red-400 rotate-180" />
                        }
                      </div>
                      <div>
                        <p className="text-sm text-white">{h.reason}</p>
                        <p className="text-[11px] text-zinc-500">
                          {new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          {h.multiplier > 1 && <span className="text-neon-yellow ml-2">{h.multiplier}x boost</span>}
                        </p>
                      </div>
                    </div>
                    <span className={`font-mono font-bold text-sm ${h.points > 0 ? "text-neon-green" : "text-red-400"}`}>
                      {h.points > 0 ? "+" : ""}{h.points} OCT
                    </span>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Spend Octane */}
        {activeTab === "spend" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="octane-spend-section">
            <div className="glass border border-neon-yellow/20 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Available Octane</p>
                  <p className="text-3xl font-heading text-neon-yellow">{loyalty?.current_points?.toLocaleString()}</p>
                </div>
                <Button onClick={() => navigate("/beats")} className="bg-neon-cyan text-black font-bold uppercase text-xs">
                  Earn More
                </Button>
              </div>
            </div>

            <h2 className="font-heading text-lg uppercase tracking-wide">Use at Checkout</h2>
            <div className="glass border border-white/5 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <ShoppingCart className="w-6 h-6 text-neon-cyan mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-white mb-1">Apply Octane as Discount</h3>
                  <p className="text-xs text-zinc-400 mb-3">
                    Redeem your Octane at checkout for instant savings. {loyalty?.octane_to_dollar || 100} Octane = $1.00 off your order.
                  </p>
                  <Button onClick={() => navigate("/cart")} variant="outline" size="sm" className="border-neon-cyan/30 text-neon-cyan text-xs">
                    Go to Cart <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-lg uppercase tracking-wide">Spend on Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: "ai_image_generation", icon: Sparkles, title: "AI Design Generation", desc: "Generate a custom AI design in the Merch Studio", link: "/merch-studio" },
                { key: "ai_beat_lab", icon: Mic2, title: "AI Beat Lab", desc: "Create AI-powered beats with custom parameters", link: "/beat-lab" },
                { key: "vocal_preview", icon: HeadphonesIcon, title: "Vocal Preview", desc: "Preview beats with AI-generated vocals", link: "/beats" },
                { key: "priority_support", icon: Zap, title: "Priority Support", desc: "Jump to the front of the support queue", link: "/support" },
              ].map((feature) => {
                const cost = loyalty?.feature_costs?.[feature.key] || 0;
                const canAfford = (loyalty?.current_points || 0) >= cost;
                return (
                  <div key={feature.key} className="glass border border-white/5 rounded-lg p-5 hover:border-neon-yellow/20 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <feature.icon className="w-5 h-5 text-neon-yellow" />
                      <span className={`text-xs font-mono font-bold ${canAfford ? "text-neon-green" : "text-zinc-500"}`}>
                        {cost} OCT
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1">{feature.title}</h3>
                    <p className="text-xs text-zinc-500 mb-3">{feature.desc}</p>
                    <Button
                      onClick={() => navigate(feature.link)}
                      size="sm"
                      variant="outline"
                      className={`text-xs ${canAfford ? "border-neon-yellow/30 text-neon-yellow" : "border-zinc-700 text-zinc-500"}`}
                    >
                      {canAfford ? "Use Feature" : "Need More Octane"} <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Leaderboard */}
        {activeTab === "leaderboard" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="octane-leaderboard-section">
            {leaderboard.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500">No leaderboard data yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry, i) => {
                  const entryTc = tierConfig[entry.tier] || tierConfig.fumes;
                  const EntryIcon = entryTc.icon;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-4 p-4 glass border rounded-lg ${
                        i < 3 ? "border-neon-yellow/20" : "border-white/5"
                      }`}
                      data-testid={`leaderboard-entry-${i}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                        i === 0 ? "bg-neon-yellow/20 text-neon-yellow" :
                        i === 1 ? "bg-zinc-300/20 text-zinc-300" :
                        i === 2 ? "bg-orange-500/20 text-orange-400" :
                        "bg-white/5 text-zinc-500"
                      }`}>
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{entry.name}</p>
                        <div className="flex items-center gap-1.5">
                          <EntryIcon className={`w-3 h-3 ${entryTc.color}`} />
                          <span className={`text-[11px] ${entryTc.color}`}>{entry.tier_label}</span>
                        </div>
                      </div>
                      <span className="font-mono font-bold text-sm text-neon-yellow">
                        {entry.lifetime_octane?.toLocaleString()} OCT
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default OctanePage;
